import { useState, useEffect, useCallback, useRef } from 'react';
import { FaSkullCrossbones } from 'react-icons/fa';
import TraitSelectorButton from './TraitSelectorButton.jsx';
import {
  healthName,
  resilienceName,
  TraitsDictionary,
} from '../../Contexts/ConstantsCollection.js';
import { useCharacter } from '../../Contexts/CharacterInfo.js';
import TextAnimationDisplay from '../../../TextAnimationDisplay.jsx';
import theme from '../../../../theme.js';

const HealthProgressBox = ({ Attributes }) => {
  const { character } = useCharacter();
  const [healRepairVal, setHealRepairVal] = useState('');

  const current =
    Attributes.name === healthName
      ? character.currentHealth
      : Attributes.name === resilienceName
        ? character.currentResilience
        : 0;

  const total =
    Attributes.name === healthName
      ? character.totalHealth
      : Attributes.name === resilienceName
        ? character.totalResilience
        : 0;

  const [percentage, setPercentage] = useState((current / total) * 100);
  const boxRef = useRef(null);

  const getShadowRate = useCallback(
    percent => {
      var rate = -1;

      if (current > 0 && percent <= 10) {
        rate = Math.max(percent * 200, 400);
      } else if (current <= 0 && current > character.deathThreshold) {
        rate = 400;
      } else if (current <= character.deathThreshold) {
        rate = 0;
      }
      return rate;
    },
    [character.deathThreshold, current]
  );

  useEffect(() => {
    var percent = current === 0 ? 0 : (current / total) * 100;
    setPercentage(percent);

    if (Attributes.name === 'Resilience') return;

    var rate = getShadowRate(percent);
    if (boxRef.current) {
      boxRef.current.style.animation = 'none';
      void boxRef.current.offsetWidth;
      boxRef.current.style.animation =
        rate === 0
          ? `deathRattle 10000ms ease-out forwards`
          : `heartBeat ${rate}ms ease-in-out infinite`;
    }
  }, [current, total, Attributes, getShadowRate, character.deathThreshold]);

  return (
    <div
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        fontWeight: 'bold',
        color: `black`,
        textAlign: 'center',
      }}
    >
      <b>{Attributes.name}</b>
      <div
        ref={boxRef}
        style={{
          height: '180px',
          width: '150px',
          minWidth: '150px',
          borderRadius: '8px',
        }}
      >
        <style>
          {`@keyframes heartBeat {
            0% {box-shadow: 0px 0px 0px rgba(0, 0, 0, 0);}
            50% {box-shadow: 0px 0px 50px rgb(from ${Attributes.selectColor} r g b / 1);}
            100% {box-shadow: 0px 0px 0px rgba(0, 0, 0, 0);}
          }`}
          {`@keyframes deathRattle {
            0% {box-shadow: 0px 0px 0px rgba(0, 0, 0, 0);}
            100% {box-shadow: 0px 0px 60px rgb(from ${theme.colors.characterDeathShadow} r g b / 1);}
          }`}
          ,
        </style>
        <div
          style={{
            height: '150px',
            width: '150px',
            minWidth: '150px',
            zIndex: '2',
            position: 'relative',
            display: 'flex',
            border: '4px solid black',
            borderBottom: '3px solid black',
            borderRadius: '8px 8px 3px 3px',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
            color: 'black',
            overflow: 'hidden',
          }}
        >
          <TextAnimationDisplay
            text={healRepairVal}
            textStyle={{
              fontSize: '4xl',
              color:
                Attributes.name === healthName
                  ? theme.colors.rightSideDark
                  : theme.colors.leftSideDark,
              sx: { WebkitTextStroke: '1px black' },
            }}
            bgColor={Attributes.baseColor}
          />
          {current >= 0 ? (
            <div
              style={{
                position: 'absolute',
                bottom: '0',
                left: '0',
                width: '100%',
                height: `${percentage}%`,
                backgroundColor: `${Attributes.baseColor}`,
              }}
            />
          ) : (
            <div
              style={{
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                height: `${(current / character.deathThreshold) * 100}%`,
                backgroundColor: `${theme.colors.characterDeath}`,
              }}
            />
          )}
          <div
            style={{
              position: 'absolute',
              zIndex: '3',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            {current <= character.deathThreshold && (
              <FaSkullCrossbones size={100} color="white" />
            )}
            <span>
              {current}/{total}
            </span>
          </div>
        </div>
        <TraitSelectorButton
          Name={Attributes.action}
          BaseColor={Attributes.baseColor}
          HighlightColor={Attributes.highlightColor}
          SelectColor={Attributes.selectColor}
          Attribute={Attributes}
          Traits={[
            TraitsDictionary.tough.leftSide,
            TraitsDictionary.belief.leftSide,
            TraitsDictionary.belief,
          ]}
          onHealRepair={val => {
            setHealRepairVal(val);
            setTimeout(() => setHealRepairVal(null), 2400);
          }}
        />
      </div>
    </div>
  );
};

export default HealthProgressBox;
